/**
 * Whether Varza can currently reach its server.
 *
 * `navigator.onLine` only reports whether the device has a network interface that thinks it is
 * connected, which is not the same question: a hotel portal, a VPN that has dropped, or a server
 * that is simply down all leave it `true`. So the flag is combined with what the API layer has
 * actually observed — one failed request marks the server unreachable, one successful request
 * marks it reachable again — and the workspace reads the combination.
 */

type Listener = () => void;

const listeners = new Set<Listener>();

let serverReachable = true;
/** When the browser last told us, or a request last proved, that the connection had returned. */
let onlineSince: number | null = Date.now();

function notify(): void {
  for (const listener of listeners) listener();
}

/**
 * Whether the device believes it has a network at all.
 *
 * This is the weaker of the two questions, and it is the right one to ask before *attempting*
 * something: a request is how the workspace learns that the server is back, so anything that
 * refuses to try while `serverReachable` is false can never discover its own mistake.
 */
export function deviceHasNetwork(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

/** The answer the workspace uses to decide what to *show*. */
export function isOnline(): boolean {
  return deviceHasNetwork() && serverReachable;
}

export function markServerReachable(): void {
  if (serverReachable) return;
  serverReachable = true;
  onlineSince = Date.now();
  notify();
}

export function markServerUnreachable(): void {
  if (!serverReachable) return;
  serverReachable = false;
  onlineSince = null;
  notify();
}

/** Milliseconds since the connection returned, or `null` while there is none. */
export function connectedForMs(): number | null {
  return isOnline() && onlineSince !== null ? Date.now() - onlineSince : null;
}

export function subscribeToConnectivity(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

let started = false;

/** Starts listening to the browser's own view of the network. Safe to call more than once. */
export function startConnectivityWatch(): void {
  if (started || typeof window === 'undefined') return;
  started = true;

  window.addEventListener('online', () => {
    // The interface is back. Whether the server answers is a separate question, and the next
    // request settles it — but assuming reachable here is what lets that request be attempted.
    serverReachable = true;
    onlineSince = Date.now();
    notify();
  });

  window.addEventListener('offline', () => {
    onlineSince = null;
    notify();
  });
}

/** Resets the module between tests. */
export function resetConnectivity(): void {
  serverReachable = true;
  onlineSince = Date.now();
  listeners.clear();
  started = false;
}
